import { useEffect, useRef, useState } from "react";
import type { SseClient } from "../../api/SseClient";
import { createSseClient } from "../../api/factory/CreateSseClient";
import type { paths } from "../../api/generated/api-types";

// RunSnapshot matches the CLR-05 active-run response shape (the SSE `run:snapshot`
// frame is byte-identical to it per CLR-09), so it is derived from the generated
// contract rather than duplicated by hand (frontend.api-types-as-contract). The
// SSE event envelopes below are hand-written: they are not part of the OpenAPI
// codegen, but the RunSnapshot they embed is.
type ActiveRunResponse =
  paths["/competitor-link-removals/active"]["get"]["responses"]["200"]["content"]["application/json"];

export type RunSnapshot = NonNullable<ActiveRunResponse["run"]>;
export type RunStatus = RunSnapshot["status"];
export type PostRemovalStatus = "pending" | "removed" | "failed" | "skipped";

export interface MatchedLink {
  href: string;
  anchorText: string;
  competitorUrl: string;
}

interface FoundPost {
  id: string;
  siteId: string;
  siteUrl: string;
  postUrl: string;
  postTitle: string;
  matchedLinks: MatchedLink[];
  removalStatus: PostRemovalStatus;
}

// One row of the live results table (донор / URL поста / найденный URL конкурента /
// анкорный текст / статус удаления). Built from `post:found` and mutated in place by
// `post:removed`.
export interface ResultRow {
  id: string;
  siteUrl: string;
  postUrl: string;
  postTitle: string;
  matchedLinks: MatchedLink[];
  removalStatus: PostRemovalStatus;
  errorDescription: string | null;
}

export interface RunLogEntry {
  message: string;
  createdAt: string;
}

// Hand-written SSE payload types (CLR-09), kept in sync with the backend manually.
interface SiteScannedPayload {
  siteId: string;
  siteUrl: string;
  postsFound: number;
  linksFound: number;
  failed: boolean;
  snapshot: RunSnapshot;
}

interface PostFoundPayload {
  post: FoundPost;
  snapshot: RunSnapshot;
}

interface PhaseChangedPayload {
  status: RunStatus;
  snapshot: RunSnapshot;
}

interface PostRemovedPayload {
  postId: string;
  siteId: string;
  removalStatus: PostRemovalStatus;
  errorDescription: string | null;
  snapshot: RunSnapshot;
}

interface RunCompletedPayload {
  snapshot: RunSnapshot;
}

interface LogPayload {
  log: { message: string; createdAt: string };
}

export type DetailState =
  | { kind: "loading" }
  | { kind: "data"; snapshot: RunSnapshot; rows: ResultRow[]; logs: RunLogEntry[] }
  | { kind: "error" };

export interface UseCompetitorLinkRemovalDetailResult {
  state: DetailState;
}

function toResultRow(post: FoundPost): ResultRow {
  return {
    id: post.id,
    siteUrl: post.siteUrl,
    postUrl: post.postUrl,
    postTitle: post.postTitle,
    matchedLinks: post.matchedLinks,
    removalStatus: post.removalStatus,
    errorDescription: null,
  };
}

function upsertRow(rows: ResultRow[], row: ResultRow): ResultRow[] {
  const exists = rows.some((existing) => existing.id === row.id);
  let next: ResultRow[];
  if (exists) {
    next = rows.map((existing) => (existing.id === row.id ? row : existing));
  } else {
    next = [...rows, row];
  }
  return next;
}

export function useCompetitorLinkRemovalDetail(
  removalId: string | undefined,
): UseCompetitorLinkRemovalDetailResult {
  const [state, setState] = useState<DetailState>({ kind: "loading" });
  const clientRef = useRef<SseClient | null>(null);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined = undefined;
    if (removalId === undefined) {
      setState({ kind: "error" });
    } else {
      const client = createSseClient({
        url: `/api/competitor-link-removals/${removalId}/subscribe`,
        events: {
          "run:snapshot"(data: unknown) {
            const snapshot = data as RunSnapshot;
            setState((previous) => {
              const next: DetailState =
                previous.kind === "data"
                  ? { ...previous, snapshot }
                  : { kind: "data", snapshot, rows: [], logs: [] };
              return next;
            });
          },

          "site:scanned"(data: unknown) {
            const payload = data as SiteScannedPayload;
            setState((previous) => {
              let next: DetailState = previous;
              if (previous.kind === "data") {
                next = { ...previous, snapshot: payload.snapshot };
              }
              return next;
            });
          },

          "post:found"(data: unknown) {
            const payload = data as PostFoundPayload;
            setState((previous) => {
              let next: DetailState = previous;
              if (previous.kind === "data") {
                next = {
                  ...previous,
                  snapshot: payload.snapshot,
                  rows: upsertRow(previous.rows, toResultRow(payload.post)),
                };
              }
              return next;
            });
          },

          "run:phase-changed"(data: unknown) {
            const payload = data as PhaseChangedPayload;
            setState((previous) => {
              let next: DetailState = previous;
              if (previous.kind === "data") {
                next = { ...previous, snapshot: payload.snapshot };
              }
              return next;
            });
          },

          "post:removed"(data: unknown) {
            const payload = data as PostRemovedPayload;
            setState((previous) => {
              let next: DetailState = previous;
              if (previous.kind === "data") {
                next = {
                  ...previous,
                  snapshot: payload.snapshot,
                  rows: previous.rows.map((row) =>
                    row.id === payload.postId
                      ? { ...row, removalStatus: payload.removalStatus, errorDescription: payload.errorDescription }
                      : row,
                  ),
                };
              }
              return next;
            });
          },

          "run:completed"(data: unknown) {
            const payload = data as RunCompletedPayload;
            setState((previous) => {
              let next: DetailState = previous;
              if (previous.kind === "data") {
                next = { ...previous, snapshot: payload.snapshot };
              }
              return next;
            });
            if (clientRef.current !== null) {
              clientRef.current.close();
              clientRef.current = null;
            }
          },

          log(data: unknown) {
            const payload = data as LogPayload;
            setState((previous) => {
              let next: DetailState = previous;
              if (previous.kind === "data") {
                next = {
                  ...previous,
                  logs: [...previous.logs, { message: payload.log.message, createdAt: payload.log.createdAt }],
                };
              }
              return next;
            });
          },
        },
        onError() {
          setState({ kind: "error" });
        },
      });

      clientRef.current = client;

      unsubscribe = () => {
        client.close();
        clientRef.current = null;
      };
    }
    return unsubscribe;
  }, [removalId]);

  return { state };
}
